import type { MouseEvent } from 'react';
import { useCallback, useEffect, useState } from 'react';
import type { Nullable, ProductEventDataList } from 'corgi-types';

import { ENERGY_EFFICIENCY_HASH } from 'common/constants/energyClass';
import { ProductEvent } from 'common/enums/Tracking';
import { dispatchTrackingEventGA4 } from 'common/tracking/dispatchTrackingEventGA4';
import { getProductClickEventData } from 'common/tracking/productEventData';
import type { ArticleTracking } from 'common/types/Article';
import { isElementClicked } from 'common/utils/event';

interface UseSlideClickHandlerArgs {
  productEventDataListType?: ProductEventDataList;
  slideIndex: number;
  articleSku: string;
  articleTracking: ArticleTracking;
  dyDecisionId?: string;
  dySlotId?: string;
}

interface UseWidthChangeOptions {
  itemsCount: number;
  slideWidth: number;
  slideGap: number;
}

const SHOWN_SLIDE_THRESHOLD = 0.75;

export const useSlideClickHandler = ({
  productEventDataListType,
  slideIndex,
  articleSku,
  articleTracking,
  dyDecisionId,
  dySlotId,
}: UseSlideClickHandlerArgs) => {
  const wishListButtonId = `reco-slider-${articleSku}`;
  const onSlideClick = useCallback(
    (event: MouseEvent<HTMLAnchorElement>) => {
      if (isElementClicked(event, `#${wishListButtonId}`)) {
        event.preventDefault();

        return;
      }

      dispatchTrackingEventGA4({
        event: ProductEvent.Click,
        item_list_name: productEventDataListType,
        items: [
          getProductClickEventData({
            dySlotId,
            dyDecisionId,
            position: slideIndex,
            tracking: articleTracking,
            list: productEventDataListType,
          }),
        ],
      });
    },
    [slideIndex, articleSku, articleTracking, productEventDataListType]
  );

  return {
    wishListButtonId,
    onSlideClick,
  };
};

export const useWidthChange = ({ itemsCount, slideWidth, slideGap }: UseWidthChangeOptions) => {
  const [shownSlides, setShownSlides] = useState(0);
  const [width, setWidth] = useState(0);

  const getShownSlides = useCallback(
    (totalWidth: number) => {
      const slideCount = totalWidth / (slideWidth + slideGap);
      const slideRatio = (totalWidth * (slideCount % 1)) / slideCount / slideWidth;

      return Math.min(
        slideRatio > SHOWN_SLIDE_THRESHOLD
          ? Math.ceil(totalWidth / (slideWidth + slideGap))
          : Math.floor(totalWidth / (slideWidth + slideGap)),
        itemsCount
      );
    },
    [itemsCount, slideWidth, slideGap]
  );

  useEffect(() => {
    setShownSlides(getShownSlides(width));
  }, [itemsCount, slideWidth, slideGap, width]);

  const handleWidthChange = (updatedWidth: Nullable<number>) => {
    if (updatedWidth) {
      setWidth(updatedWidth);
    }
  };

  return { shownSlides, handleWidthChange };
};

export const useEnergyLabelHover = (url: string) => {
  const [energyLabelHover, setEnergyLabelHover] = useState(false);
  const DATA_TESTID = 'article-energy';
  const urlWithHash = `${url}${energyLabelHover ? ENERGY_EFFICIENCY_HASH : ''}`;
  const handleMouseOver = useCallback((event: MouseEvent) => {
    setEnergyLabelHover((event.target as HTMLElement).dataset.testid === DATA_TESTID);
  }, []);

  return { urlWithHash, handleMouseOver };
};
